// Virtualizes final projected rows without recovering event-specific meaning
// in the component layer. Expanded members stay independent top-level rows.
import { useVirtualizer } from '@tanstack/react-virtual';
import {
    Fragment,
    type ReactNode,
    type Ref,
    type UIEvent,
    useCallback,
    useId,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { ScrollToBottomButton } from '@apps/web/components/ui/scroll_to_bottom_button';
import { type TranscriptViewRow } from '@apps/web/features/phoebe_chat_v3/model/activity';
import { type ProjectedTranscriptRow } from '@apps/web/features/phoebe_chat_v3/registry/types';
import { ActivityGroup } from '@apps/web/features/phoebe_chat_v3/ui/activity_group';
import { CHAT_COLUMN_CLASS } from '@apps/web/features/phoebe_chat_v3/ui/chat_column';
import {
    type AssistantTurnPosition,
    AssistantTurnPositionContext,
    assistantTurnPositions,
    continuationOf,
    type MessageContinuation,
    MessageContinuationContext,
    messageSenderOf,
    NO_MESSAGE_CONTINUATION,
} from '@apps/web/features/phoebe_chat_v3/ui/message_continuation_context';
import { EventLine } from '@apps/web/features/phoebe_chat_v3/ui/primitives/event_line';
import { formatDateKeyLong } from '@apps/web/lib/date_utils';
import { cn } from '@apps/web/lib/utils';

const ESTIMATED_ROW_HEIGHT_PX = 72;
const OVERSCAN_ROW_COUNT = 12;
const STICKY_BOTTOM_THRESHOLD_PX = 64;
const MAX_CORRECTION_PASS_COUNT = 2;
const INITIAL_BOTTOM_CORRECTION_PASS_COUNT = 6;

export type TranscriptRowRenderer = (row: TranscriptViewRow) => ReactNode;

export type TranscriptRestorationDescriptor = Readonly<{
    readonly committedEventId: string;
    readonly intraRowOffset: number;
}>;

export type VirtualizedTranscriptCapture = Readonly<{
    readonly capture: () => TranscriptRestorationDescriptor | null;
}>;

type TranscriptPositionProps = Readonly<{
    readonly captureRef?: Ref<VirtualizedTranscriptCapture>;
    readonly initialDescriptor?: TranscriptRestorationDescriptor | null;
    readonly onPositionChange?: (
        descriptor: TranscriptRestorationDescriptor,
    ) => void;
}>;

type SharedVirtualizedTranscriptProps = TranscriptPositionProps;

type LegacyVirtualizedTranscriptProps = SharedVirtualizedTranscriptProps &
    Readonly<{
        readonly rows: ReadonlyArray<TranscriptViewRow>;
        readonly renderRow: TranscriptRowRenderer;
    }>;

type ProjectedVirtualizedTranscriptProps = SharedVirtualizedTranscriptProps &
    Readonly<{
        readonly onGroupToggle: (groupId: string) => void;
        readonly waitingForInput?: boolean;
        readonly rows: ReadonlyArray<ProjectedTranscriptRow>;
    }>;

export type VirtualizedTranscriptProps =
    | LegacyVirtualizedTranscriptProps
    | ProjectedVirtualizedTranscriptProps;

type ViewportAnchor = Readonly<{
    readonly intraRowOffset: number;
    readonly rowKey: string;
}>;

type RowSnapshot = Readonly<{
    readonly expandedByGroupId: ReadonlyMap<string, boolean>;
    readonly memberCountByGroupId: ReadonlyMap<string, number>;
    readonly rowKeys: ReadonlyArray<string>;
    readonly signature: string;
    readonly visibleMemberCountByGroupId: ReadonlyMap<string, number>;
}>;

type PendingRowChange = Readonly<{
    readonly currentSignature: string;
    readonly previousSignature: string;
    readonly savedAnchor: ViewportAnchor | null;
    readonly wasSticky: boolean;
}>;

type ProgrammaticScroll = Readonly<{
    readonly expectedScrollTopPx: number;
}>;

type PositionCorrectionResult =
    | 'applied'
    | 'target_unavailable'
    | 'waiting_for_mount';

type RestorationState = Readonly<{
    readonly descriptorKey: string;
    readonly status: 'applied' | 'cancelled' | 'pending' | 'unavailable';
}>;

function rowKeyAtIndex(
    props: VirtualizedTranscriptProps,
    index: number,
): string {
    if ('renderRow' in props) {
        const row = props.rows[index];
        if (row === undefined) {
            throw new Error(`Missing transcript row at index ${index}.`);
        }
        return row.rowKey;
    }
    const row = props.rows[index];
    if (row === undefined) {
        throw new Error(`Missing projected row at index ${index}.`);
    }
    return row.rowId;
}

function groupExpansionAtIndex(
    props: VirtualizedTranscriptProps,
    index: number,
): Readonly<{
    readonly expanded: boolean;
    readonly groupId: string;
    readonly memberCount: number;
}> | null {
    const row = props.rows[index];
    if (row === undefined || row.kind !== 'activity_group_summary') {
        return null;
    }
    return {
        expanded: row.expanded,
        groupId: row.groupId,
        memberCount: row.memberCount,
    };
}

function groupMemberIdAtIndex(
    props: VirtualizedTranscriptProps,
    index: number,
): string | null {
    const row = props.rows[index];
    return row?.kind === 'activity_group_member' ? row.groupId : null;
}

function rowSnapshot(props: VirtualizedTranscriptProps): RowSnapshot {
    const rowKeys: Array<string> = [];
    const expandedByGroupId = new Map<string, boolean>();
    const memberCountByGroupId = new Map<string, number>();
    const visibleMemberCountByGroupId = new Map<string, number>();
    for (let index = 0; index < props.rows.length; index += 1) {
        rowKeys.push(rowKeyAtIndex(props, index));
        const groupExpansion = groupExpansionAtIndex(props, index);
        if (groupExpansion !== null) {
            expandedByGroupId.set(
                groupExpansion.groupId,
                groupExpansion.expanded,
            );
            memberCountByGroupId.set(
                groupExpansion.groupId,
                groupExpansion.memberCount,
            );
        }
        const memberGroupId = groupMemberIdAtIndex(props, index);
        if (memberGroupId !== null) {
            visibleMemberCountByGroupId.set(
                memberGroupId,
                (visibleMemberCountByGroupId.get(memberGroupId) ?? 0) + 1,
            );
        }
    }
    const expansionSignature = Array.from(expandedByGroupId.entries())
        .map(
            ([groupId, expanded]) =>
                `${groupId}:${String(expanded)}:${String(memberCountByGroupId.get(groupId) ?? 0)}`,
        )
        .join('\u0000');
    return {
        expandedByGroupId,
        memberCountByGroupId,
        rowKeys,
        signature: `${rowKeys.join('\u0000')}\u0001${expansionSignature}`,
        visibleMemberCountByGroupId,
    };
}

function toggledGroupIds(
    previous: RowSnapshot,
    current: RowSnapshot,
): ReadonlySet<string> {
    const toggled = new Set<string>();
    const groupIds = new Set([
        ...previous.expandedByGroupId.keys(),
        ...current.expandedByGroupId.keys(),
    ]);
    for (const groupId of groupIds) {
        if (
            previous.expandedByGroupId.get(groupId) !==
            current.expandedByGroupId.get(groupId)
        ) {
            toggled.add(groupId);
        }
    }
    return toggled;
}

function newlyProjectedRowCount(
    previous: RowSnapshot,
    current: RowSnapshot,
    toggledGroups: ReadonlySet<string>,
): number {
    const previousRowKeys = new Set(previous.rowKeys);
    const currentRowKeys = new Set(current.rowKeys);
    let addedRowCount = current.rowKeys.filter(
        (rowKey) => !previousRowKeys.has(rowKey),
    ).length;
    let removedRowCount = previous.rowKeys.filter(
        (rowKey) => !currentRowKeys.has(rowKey),
    ).length;

    for (const groupId of toggledGroups) {
        const wasExpanded = previous.expandedByGroupId.get(groupId);
        const isExpanded = current.expandedByGroupId.get(groupId);
        if (wasExpanded === false && isExpanded === true) {
            const revealedExistingMemberCount = Math.min(
                previous.memberCountByGroupId.get(groupId) ?? 0,
                current.visibleMemberCountByGroupId.get(groupId) ?? 0,
            );
            addedRowCount = Math.max(
                0,
                addedRowCount - revealedExistingMemberCount,
            );
        } else if (wasExpanded === true && isExpanded === false) {
            removedRowCount = Math.max(
                0,
                removedRowCount -
                    (previous.visibleMemberCountByGroupId.get(groupId) ?? 0),
            );
        }
    }

    // A pending row reconciling to a committed identity replaces one visible
    // projection. Only additions with no removed counterpart are new rows.
    return Math.max(0, addedRowCount - removedRowCount);
}

function virtualRowElements(viewport: HTMLElement): Array<HTMLElement> {
    return Array.from(
        viewport.querySelectorAll<HTMLElement>('[data-virtual-row]'),
    );
}

function rowElement(viewport: HTMLElement, rowKey: string): HTMLElement | null {
    return (
        virtualRowElements(viewport).find(
            (element) => element.dataset['rowKey'] === rowKey,
        ) ?? null
    );
}

function identityValue(identity: string, marker: string): string | null {
    const markerIndex = identity.lastIndexOf(marker);
    if (markerIndex < 0) {
        return null;
    }
    return identity.slice(markerIndex + marker.length);
}

function committedEventIdAtIndex(
    props: VirtualizedTranscriptProps,
    index: number,
): string | null {
    if ('renderRow' in props) {
        const row = props.rows[index];
        if (row === undefined) {
            return null;
        }
        if (
            (row.kind === 'occurrence' ||
                row.kind === 'activity_group_member') &&
            row.occurrence.kind === 'committed'
        ) {
            return row.occurrence.eventId;
        }
        return identityValue(row.rowKey, 'committed-event:');
    }
    const row = props.rows[index];
    if (
        row === undefined ||
        row.kind === 'day_heading' ||
        row.kind === 'placeholder'
    ) {
        return null;
    }

    const occurrenceIdentity =
        row.kind === 'activity_group_summary' ? row.rowId : row.occurrenceId;
    if (
        identityValue(row.rowId, 'pending-event:') !== null ||
        identityValue(occurrenceIdentity, 'pending-event:') !== null
    ) {
        return null;
    }
    const committedEventId =
        identityValue(row.rowId, 'committed-event:') ??
        identityValue(occurrenceIdentity, 'committed-event:');
    if (committedEventId !== null) {
        return committedEventId;
    }

    const localOperationId =
        identityValue(row.rowId, 'local-operation:') ??
        identityValue(occurrenceIdentity, 'local-operation:');
    if (localOperationId === null) {
        return row.eventIds[0] ?? null;
    }
    return row.eventIds.find((eventId) => eventId !== localOperationId) ?? null;
}

function precedingCommittedAnchor(
    props: VirtualizedTranscriptProps,
    viewport: HTMLElement,
): Readonly<ViewportAnchor & { readonly committedEventId: string }> | null {
    const viewportTopPx = viewport.getBoundingClientRect().top;
    let nearestRowTopPx = Number.NEGATIVE_INFINITY;
    let anchor: Readonly<
        ViewportAnchor & { readonly committedEventId: string }
    > | null = null;

    for (const element of virtualRowElements(viewport)) {
        const indexValue = element.dataset['index'];
        const rowKey = element.dataset['rowKey'];
        if (indexValue === undefined || rowKey === undefined) {
            continue;
        }
        const index = Number(indexValue);
        if (!Number.isInteger(index)) {
            continue;
        }
        const rowTopPx = element.getBoundingClientRect().top;
        if (rowTopPx > viewportTopPx || rowTopPx < nearestRowTopPx) {
            continue;
        }
        const committedEventId = committedEventIdAtIndex(props, index);
        if (committedEventId === null) {
            continue;
        }
        nearestRowTopPx = rowTopPx;
        anchor = {
            committedEventId,
            intraRowOffset: viewportTopPx - rowTopPx,
            rowKey,
        };
    }
    return anchor;
}

function viewportIsSticky(viewport: HTMLElement): boolean {
    const distanceToBottomPx =
        viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
    return distanceToBottomPx <= STICKY_BOTTOM_THRESHOLD_PX;
}

function rowIndexForCommittedEvent(
    props: VirtualizedTranscriptProps,
    committedEventId: string,
): number {
    for (let index = 0; index < props.rows.length; index += 1) {
        if (committedEventIdAtIndex(props, index) === committedEventId) {
            return index;
        }
        if ('renderRow' in props) {
            continue;
        }
        const row = props.rows[index];
        if (
            row !== undefined &&
            'eventIds' in row &&
            row.eventIds.includes(committedEventId)
        ) {
            return index;
        }
    }
    return -1;
}

function captureRestorationDescriptor(
    props: VirtualizedTranscriptProps,
    viewport: HTMLElement,
): TranscriptRestorationDescriptor | null {
    const anchor = precedingCommittedAnchor(props, viewport);
    if (anchor === null) {
        return null;
    }
    return {
        committedEventId: anchor.committedEventId,
        intraRowOffset: anchor.intraRowOffset,
    };
}

function transcriptRowBottomPadding(kind: string): string {
    switch (kind) {
        case 'activity_group_member':
            return 'pb-0.5';
        case 'activity_group_summary':
            return 'pb-2.5';
        default:
            return 'pb-3';
    }
}

// Four gaps: pb-1 within a person's run, pb-3 between like rows, pb-4 under
// an action row, pb-5 between unlike rows.
function messageRowBottomPadding(
    row: ProjectedTranscriptRow,
    nextRow: ProjectedTranscriptRow | undefined,
    continuation: MessageContinuation,
): string {
    const sender = messageSenderOf(row);
    if (continuation.continuedByNext) {
        return sender === 'assistant' ? 'pb-3' : 'pb-1';
    }
    const nextIsCard =
        nextRow?.kind === 'occurrence' && nextRow.isCard === true;
    if (sender !== null) {
        if (continuation.followedByCard !== true) {
            return 'pb-4';
        }
        return nextIsCard ? 'pb-3' : 'pb-5';
    }
    if (
        row.kind === 'occurrence' &&
        nextRow !== undefined &&
        (messageSenderOf(nextRow) !== null ||
            (row.isCard === true) !== nextIsCard)
    ) {
        return 'pb-5';
    }
    return transcriptRowBottomPadding(row.kind);
}

function renderProjectedRow(
    row: ProjectedTranscriptRow,
    onGroupToggle: (groupId: string) => void,
    waitingForInput: boolean,
): ReactNode {
    switch (row.kind) {
        case 'activity_group_member':
            return (
                <EventLine className="min-w-0" glyph={<span />}>
                    {row.node}
                </EventLine>
            );
        case 'occurrence':
            return row.node;
        case 'activity_group_summary':
            return (
                <ActivityGroup
                    waitingForInput={waitingForInput}
                    row={{
                        kind: 'activity_group_summary',
                        rowKey: row.rowId,
                        groupId: row.groupId,
                        summary: row.summary,
                        memberCount: row.memberCount,
                        expanded: row.expanded,
                        startedAtMs: row.startedAtMs,
                        live: row.live,
                    }}
                />
            );
        case 'day_heading': {
            const label = formatDateKeyLong(row.day, 'UTC');
            return (
                <div
                    role="separator"
                    aria-label={label}
                    className="py-1 text-center"
                >
                    <span className="text-muted-foreground text-v3-chat-meta font-medium">
                        {label}
                    </span>
                </div>
            );
        }
        case 'placeholder':
            return <span>{row.customerCopy}</span>;
        default: {
            const exhaustiveRow: never = row;
            return exhaustiveRow;
        }
    }
}

export function VirtualizedTranscript(
    props: VirtualizedTranscriptProps,
): ReactNode {
    const rows = props.rows;
    const currentSnapshot = useMemo(() => rowSnapshot(props), [props]);
    const projectedRows = 'renderRow' in props ? null : props.rows;
    const turnPositions = useMemo(
        () =>
            projectedRows === null
                ? null
                : assistantTurnPositions(projectedRows),
        [projectedRows],
    );
    const propsRef = useRef(props);
    const currentSnapshotRef = useRef(currentSnapshot);
    const scrollElementRef = useRef<HTMLDivElement | null>(null);
    const anchorRef = useRef<ViewportAnchor | null>(null);
    const committedPropsRef = useRef(props);
    const committedSnapshotRef = useRef<RowSnapshot | null>(null);
    const pendingRowChangeRef = useRef<PendingRowChange | null>(null);
    const startsAtBottom =
        props.initialDescriptor === null ||
        props.initialDescriptor === undefined;
    const stickyRef = useRef(startsAtBottom);
    const correctionFrameRef = useRef<number | null>(null);
    const programmaticScrollRef = useRef<ProgrammaticScroll | null>(null);
    const recentProgrammaticScrollTopRef = useRef<number | null>(null);
    const programmaticScrollExpiryFrameRef = useRef<number | null>(null);
    const restorationStateRef = useRef<RestorationState | null>(null);
    const [isSticky, setIsSticky] = useState(startsAtBottom);
    const [unseenRowCount, setUnseenRowCount] = useState(0);
    const activityDescriptionId = `${useId()}-new-activity-count`;

    const committedSnapshot = committedSnapshotRef.current;
    if (
        committedSnapshot !== null &&
        committedSnapshot.signature !== currentSnapshot.signature &&
        pendingRowChangeRef.current?.currentSignature !==
            currentSnapshot.signature
    ) {
        const viewport = scrollElementRef.current;
        pendingRowChangeRef.current = {
            currentSignature: currentSnapshot.signature,
            previousSignature: committedSnapshot.signature,
            savedAnchor:
                viewport === null
                    ? anchorRef.current
                    : precedingCommittedAnchor(
                          committedPropsRef.current,
                          viewport,
                      ),
            wasSticky:
                viewport === null
                    ? stickyRef.current
                    : viewportIsSticky(viewport),
        };
    }
    propsRef.current = props;
    currentSnapshotRef.current = currentSnapshot;

    const clearProgrammaticScroll = useCallback(() => {
        programmaticScrollRef.current = null;
        recentProgrammaticScrollTopRef.current = null;
        if (programmaticScrollExpiryFrameRef.current === null) {
            return;
        }
        window.cancelAnimationFrame(programmaticScrollExpiryFrameRef.current);
        programmaticScrollExpiryFrameRef.current = null;
    }, []);

    const scrollProgrammatically = useCallback(
        (scrollTopPx: number, behavior: ScrollBehavior = 'auto') => {
            const viewport = scrollElementRef.current;
            if (viewport === null) {
                return;
            }
            // Browsers clamp out-of-range scroll writes. Track the clamped
            // value so that the resulting scroll event is not mistaken for
            // reader input and does not cancel a pending measurement pass.
            const targetScrollTopPx = Math.min(
                Math.max(0, scrollTopPx),
                Math.max(0, viewport.scrollHeight - viewport.clientHeight),
            );
            const programmaticScroll = {
                expectedScrollTopPx: targetScrollTopPx,
            };
            clearProgrammaticScroll();
            programmaticScrollRef.current = programmaticScroll;
            viewport.scrollTo({ top: targetScrollTopPx, behavior });
            if (programmaticScrollRef.current !== programmaticScroll) {
                return;
            }
            // A marker belongs to one expected scroll event. Expiring it on
            // the next frame prevents a missing event from masking later
            // reader input at an old target.
            const expiryFrame = window.requestAnimationFrame(() => {
                if (programmaticScrollRef.current === programmaticScroll) {
                    programmaticScrollRef.current = null;
                }
                if (
                    recentProgrammaticScrollTopRef.current === targetScrollTopPx
                ) {
                    recentProgrammaticScrollTopRef.current = null;
                }
                if (programmaticScrollExpiryFrameRef.current === expiryFrame) {
                    programmaticScrollExpiryFrameRef.current = null;
                }
            });
            programmaticScrollExpiryFrameRef.current = expiryFrame;
        },
        [clearProgrammaticScroll],
    );
    const virtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => scrollElementRef.current,
        estimateSize: () => ESTIMATED_ROW_HEIGHT_PX,
        getItemKey: (index) => rowKeyAtIndex(props, index),
        measureElement: (element) => element.getBoundingClientRect().height,
        overscan: OVERSCAN_ROW_COUNT,
        scrollToFn: (offset, { adjustments = 0, behavior }) => {
            scrollProgrammatically(offset + adjustments, behavior);
        },
    });
    const measureVirtualRow = useCallback(
        (element: HTMLDivElement | null) => {
            if (element === null) {
                return;
            }
            const indexValue = element.dataset['index'];
            if (indexValue === undefined) {
                throw new Error(
                    'A mounted transcript row must expose its index.',
                );
            }
            const index = Number(indexValue);
            if (!Number.isInteger(index)) {
                throw new Error(
                    `Invalid mounted transcript row index ${indexValue}.`,
                );
            }
            // The virtualizer defers sync measurement during reader scrolls.
            // Size every committed mount now, then retain its observer for
            // late layout changes such as images or expanded content.
            virtualizer.measureElement(element);
            virtualizer.resizeItem(
                index,
                element.getBoundingClientRect().height,
            );
        },
        [virtualizer],
    );
    const virtualRows = virtualizer.getVirtualItems();
    const initialDescriptor = props.initialDescriptor ?? null;
    const initialDescriptorKey =
        initialDescriptor === null
            ? null
            : `${initialDescriptor.committedEventId}\u0000${String(initialDescriptor.intraRowOffset)}`;

    const capturePosition =
        useCallback((): TranscriptRestorationDescriptor | null => {
            const viewport = scrollElementRef.current;
            if (viewport === null) {
                return null;
            }
            return captureRestorationDescriptor(propsRef.current, viewport);
        }, []);

    useImperativeHandle(
        props.captureRef,
        () => ({ capture: capturePosition }),
        [capturePosition],
    );

    const publishPosition = useCallback(() => {
        const descriptor = capturePosition();
        if (descriptor !== null) {
            propsRef.current.onPositionChange?.(descriptor);
        }
    }, [capturePosition]);

    const cancelPositionCorrection = useCallback(() => {
        clearProgrammaticScroll();
        if (correctionFrameRef.current === null) {
            return;
        }
        window.cancelAnimationFrame(correctionFrameRef.current);
        correctionFrameRef.current = null;
    }, [clearProgrammaticScroll]);

    const schedulePositionCorrection = useCallback(
        (
            target: ViewportAnchor | 'bottom',
            restorationDescriptorKey: string | null = null,
            correctionPassCount = MAX_CORRECTION_PASS_COUNT,
        ) => {
            const pendingRestoration = restorationStateRef.current;
            if (
                pendingRestoration?.status === 'pending' &&
                pendingRestoration.descriptorKey !== restorationDescriptorKey
            ) {
                restorationStateRef.current = {
                    descriptorKey: pendingRestoration.descriptorKey,
                    status: 'cancelled',
                };
            }
            cancelPositionCorrection();
            const viewport = scrollElementRef.current;
            if (viewport === null) {
                return;
            }
            const scrollViewport: HTMLDivElement = viewport;

            function correctPosition(): PositionCorrectionResult {
                if (target === 'bottom') {
                    scrollProgrammatically(
                        scrollViewport.scrollHeight -
                            scrollViewport.clientHeight,
                    );
                    return 'applied';
                }
                const snapshot = currentSnapshotRef.current;
                const rowIndex = snapshot.rowKeys.indexOf(target.rowKey);
                if (rowIndex < 0) {
                    return 'target_unavailable';
                }
                const element = rowElement(scrollViewport, target.rowKey);
                if (element === null) {
                    virtualizer.scrollToIndex(rowIndex, { align: 'start' });
                    return 'waiting_for_mount';
                }
                const viewportRectangle =
                    scrollViewport.getBoundingClientRect();
                const rowRectangle = element.getBoundingClientRect();
                virtualizer.scrollToOffset(
                    scrollViewport.scrollTop +
                        rowRectangle.top -
                        viewportRectangle.top +
                        target.intraRowOffset,
                    { align: 'start' },
                );
                return 'applied';
            }

            function finishCorrection(): void {
                correctionFrameRef.current = null;
                if (target === 'bottom') {
                    stickyRef.current = true;
                    anchorRef.current = null;
                    setIsSticky(true);
                    setUnseenRowCount(0);
                } else {
                    stickyRef.current = false;
                    anchorRef.current = precedingCommittedAnchor(
                        propsRef.current,
                        scrollViewport,
                    );
                    setIsSticky(false);
                }
                if (restorationDescriptorKey !== null) {
                    restorationStateRef.current = {
                        descriptorKey: restorationDescriptorKey,
                        status: 'applied',
                    };
                }
                publishPosition();
            }

            let completedPassCount = 0;
            function correctOnAnimationFrame(): void {
                const result = correctPosition();
                if (result === 'target_unavailable') {
                    correctionFrameRef.current = null;
                    if (restorationDescriptorKey !== null) {
                        restorationStateRef.current = {
                            descriptorKey: restorationDescriptorKey,
                            status: 'unavailable',
                        };
                    }
                    return;
                }
                if (result === 'waiting_for_mount') {
                    // Mount navigation is not a measurement correction. Keep
                    // waking the virtualizer until the stable row can receive
                    // its two measured passes.
                    correctionFrameRef.current = window.requestAnimationFrame(
                        correctOnAnimationFrame,
                    );
                    return;
                }
                completedPassCount += 1;
                if (completedPassCount < correctionPassCount) {
                    correctionFrameRef.current = window.requestAnimationFrame(
                        correctOnAnimationFrame,
                    );
                    return;
                }
                finishCorrection();
            }

            // Ref measurement runs during commit. One immediate pass and one
            // animation-frame pass cover that geometry and one late report.
            correctOnAnimationFrame();
        },
        [
            cancelPositionCorrection,
            publishPosition,
            scrollProgrammatically,
            virtualizer,
        ],
    );

    const updateStickyState = useCallback(
        (event: UIEvent<HTMLDivElement>) => {
            const viewport = event.currentTarget;
            const programmaticScroll = programmaticScrollRef.current;
            if (programmaticScroll !== null) {
                if (
                    Math.abs(
                        viewport.scrollTop -
                            programmaticScroll.expectedScrollTopPx,
                    ) <= 1
                ) {
                    // The expected event consumes its pending marker. An exact
                    // duplicate may follow for coalesced writes, but only
                    // until the marker's next-frame expiry.
                    programmaticScrollRef.current = null;
                    recentProgrammaticScrollTopRef.current =
                        programmaticScroll.expectedScrollTopPx;
                    return;
                }
                clearProgrammaticScroll();
            } else if (
                recentProgrammaticScrollTopRef.current !== null &&
                Math.abs(
                    viewport.scrollTop - recentProgrammaticScrollTopRef.current,
                ) <= 1
            ) {
                return;
            } else {
                clearProgrammaticScroll();
            }

            const restorationState = restorationStateRef.current;
            if (restorationState?.status === 'pending') {
                restorationStateRef.current = {
                    descriptorKey: restorationState.descriptorKey,
                    status: 'cancelled',
                };
            }
            cancelPositionCorrection();
            const sticky = viewportIsSticky(viewport);
            stickyRef.current = sticky;
            setIsSticky(sticky);
            if (sticky) {
                anchorRef.current = null;
                setUnseenRowCount(0);
            } else {
                anchorRef.current = precedingCommittedAnchor(
                    propsRef.current,
                    viewport,
                );
            }
            const descriptor = captureRestorationDescriptor(
                propsRef.current,
                viewport,
            );
            if (descriptor !== null) {
                propsRef.current.onPositionChange?.(descriptor);
            }
        },
        [cancelPositionCorrection, clearProgrammaticScroll],
    );

    const showNewestActivity = useCallback(() => {
        stickyRef.current = true;
        setIsSticky(true);
        setUnseenRowCount(0);
        schedulePositionCorrection('bottom');
    }, [schedulePositionCorrection]);

    useLayoutEffect(() => {
        const viewport = scrollElementRef.current;
        if (viewport === null) {
            return;
        }
        const previousSnapshot = committedSnapshotRef.current;
        const snapshot = currentSnapshotRef.current;
        const pendingRowChange = pendingRowChangeRef.current;
        committedSnapshotRef.current = snapshot;
        committedPropsRef.current = propsRef.current;
        if (
            pendingRowChange?.currentSignature === snapshot.signature &&
            pendingRowChange.previousSignature === previousSnapshot?.signature
        ) {
            pendingRowChangeRef.current = null;
        }

        if (
            initialDescriptor !== null &&
            initialDescriptorKey !== null &&
            restorationStateRef.current?.descriptorKey !== initialDescriptorKey
        ) {
            const rowIndex = rowIndexForCommittedEvent(
                propsRef.current,
                initialDescriptor.committedEventId,
            );
            const rowKey = snapshot.rowKeys[rowIndex];
            if (rowKey !== undefined) {
                restorationStateRef.current = {
                    descriptorKey: initialDescriptorKey,
                    status: 'pending',
                };
                setUnseenRowCount(0);
                schedulePositionCorrection(
                    {
                        intraRowOffset: initialDescriptor.intraRowOffset,
                        rowKey,
                    },
                    initialDescriptorKey,
                );
                return;
            }
        }

        if (previousSnapshot === null) {
            schedulePositionCorrection(
                'bottom',
                null,
                INITIAL_BOTTOM_CORRECTION_PASS_COUNT,
            );
            return;
        }
        if (previousSnapshot.signature === snapshot.signature) {
            return;
        }

        const wasSticky =
            pendingRowChange?.currentSignature === snapshot.signature
                ? pendingRowChange.wasSticky
                : viewportIsSticky(viewport);
        const savedAnchor =
            pendingRowChange?.currentSignature === snapshot.signature
                ? pendingRowChange.savedAnchor
                : precedingCommittedAnchor(committedPropsRef.current, viewport);
        const toggledGroups = toggledGroupIds(previousSnapshot, snapshot);
        const addedRowCount = newlyProjectedRowCount(
            previousSnapshot,
            snapshot,
            toggledGroups,
        );
        if (wasSticky) {
            setUnseenRowCount(0);
            schedulePositionCorrection('bottom');
            return;
        }
        stickyRef.current = false;
        setIsSticky(false);
        if (addedRowCount > 0) {
            setUnseenRowCount((currentCount) => currentCount + addedRowCount);
        }
        if (savedAnchor !== null) {
            schedulePositionCorrection(savedAnchor);
            return;
        }
        anchorRef.current = precedingCommittedAnchor(
            propsRef.current,
            viewport,
        );
        publishPosition();
    }, [
        currentSnapshot.signature,
        initialDescriptor,
        initialDescriptorKey,
        publishPosition,
        schedulePositionCorrection,
    ]);

    useLayoutEffect(() => cancelPositionCorrection, [cancelPositionCorrection]);

    return (
        <div className="relative h-full min-h-0 min-w-0">
            {/* Keyboard users need to focus this overflow region to scroll it. */}
            <div
                ref={scrollElementRef}
                role="region"
                aria-label="Conversation transcript"
                tabIndex={0}
                data-testid="virtualized-transcript"
                data-modeled-row-count={rows.length}
                className="focus-visible:ring-ring/50 h-full min-h-0 min-w-0 overflow-x-hidden overflow-y-auto outline-none focus-visible:ring-[3px]"
                onScroll={updateStickyState}
            >
                <div className={CHAT_COLUMN_CLASS}>
                    <div
                        data-testid="virtualized-transcript-content"
                        className="relative"
                        style={{ height: virtualizer.getTotalSize() }}
                    >
                        {virtualRows.map((virtualRow) => {
                            let rowKey: string;
                            let node: ReactNode;
                            let bottomPadding: string;
                            let continuation: MessageContinuation;
                            let turnPosition: AssistantTurnPosition | null;
                            if ('renderRow' in props) {
                                const row = props.rows[virtualRow.index];
                                if (row === undefined) {
                                    return null;
                                }
                                rowKey = row.rowKey;
                                bottomPadding = transcriptRowBottomPadding(
                                    row.kind,
                                );
                                continuation = NO_MESSAGE_CONTINUATION;
                                turnPosition = null;
                                node = props.renderRow(row);
                            } else {
                                const row = props.rows[virtualRow.index];
                                if (row === undefined) {
                                    return null;
                                }
                                rowKey = row.rowId;
                                continuation = continuationOf(
                                    props.rows,
                                    virtualRow.index,
                                );
                                turnPosition =
                                    turnPositions?.get(row.rowId) ?? null;
                                bottomPadding = messageRowBottomPadding(
                                    row,
                                    props.rows[virtualRow.index + 1],
                                    continuation,
                                );
                                node = renderProjectedRow(
                                    row,
                                    props.onGroupToggle,
                                    props.waitingForInput === true,
                                );
                            }
                            return (
                                <div
                                    key={virtualRow.key}
                                    ref={measureVirtualRow}
                                    data-index={virtualRow.index}
                                    data-virtual-row={true}
                                    data-row-key={rowKey}
                                    className={cn(
                                        'absolute top-0 left-0 w-full max-w-full min-w-0',
                                        bottomPadding,
                                        virtualRow.index === 0 && 'pt-4',
                                    )}
                                    style={{
                                        transform: `translateY(${virtualRow.start}px)`,
                                    }}
                                >
                                    <MessageContinuationContext.Provider
                                        value={continuation}
                                    >
                                        <AssistantTurnPositionContext.Provider
                                            value={turnPosition}
                                        >
                                            {node}
                                        </AssistantTurnPositionContext.Provider>
                                    </MessageContinuationContext.Provider>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            {!isSticky && (
                <Fragment>
                    <ScrollToBottomButton
                        aria-label={
                            unseenRowCount > 0
                                ? 'New activity'
                                : 'Scroll to bottom'
                        }
                        aria-describedby={
                            unseenRowCount > 0
                                ? activityDescriptionId
                                : undefined
                        }
                        className="bottom-4"
                        onClick={showNewestActivity}
                    />
                    {unseenRowCount > 0 && (
                        <span
                            id={activityDescriptionId}
                            aria-live="polite"
                            className="sr-only"
                        >
                            {unseenRowCount} new projected rows
                        </span>
                    )}
                </Fragment>
            )}
        </div>
    );
}
